import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../ui';

export interface FormField {
  id: string;
  label: string;
  type: 'text' | 'select' | 'textarea' | 'number' | 'checkbox';
  value: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  min?: number;
  max?: number;
  step?: number;
}

interface ToolConfigurationPanelProps {
  title?: string;
  description?: string;
  fields: FormField[];
  onFieldChange: (fieldId: string, value: string) => void;
  className?: string;
}

export const ToolConfigurationPanel: React.FC<ToolConfigurationPanelProps> = ({
  title,
  description,
  fields,
  onFieldChange,
  className = '',
}) => {
  const { t } = useTranslation();

  const displayTitle = title || t('tools.configuration.title');
  const displayDescription =
    description || t('tools.configuration.description');
  const renderField = (field: FormField) => {
    const baseClasses =
      'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100';

    switch (field.type) {
      case 'select':
        return (
          <select
            value={field.value}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={field.disabled}
            className={baseClasses}
          >
            <option value="">
              {t('tools.configuration.selectOption', {
                field: field.label.toLowerCase(),
              })}
            </option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={field.value}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled}
            className={`${baseClasses} min-h-[100px] resize-vertical`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={field.value}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled}
            min={field.min}
            max={field.max}
            step={field.step}
            className={baseClasses}
          />
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={field.value === 'true'}
              onChange={(e) =>
                onFieldChange(field.id, e.target.checked ? 'true' : 'false')
              }
              disabled={field.disabled}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            {field.helpText && (
              <span className="text-sm text-gray-500">{field.helpText}</span>
            )}
          </div>
        );

      default: // text
        return (
          <input
            type="text"
            value={field.value}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled}
            className={baseClasses}
          />
        );
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg sm:text-xl">{displayTitle}</CardTitle>
        <CardDescription className="text-sm sm:text-base">
          {displayDescription}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((field) => (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label} {field.required && '*'}
            </label>
            {renderField(field)}
            {field.error && (
              <p className="mt-1 text-sm text-red-600">{field.error}</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
